import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styles from './OrderShipmentForm.module.scss';
import Input from '../../blocks/Input';
import Select from '../../common/Select';
import {
  Carriers,
  ShippingMethodsByCarrier,
  CarriersDisplayName,
  findCarrierByShippingMethod,
} from '../../../constants/shippingMethods';
import { startCase } from 'lodash';
import InputNumber from 'rc-input-number';
import { numberFormatter, stringOrNAFormatter } from '../../../utils/formatHelper';
import { VARIANT_IDENTIFIER_SKU, VariantIdentifierDisplayName } from '../../../constants/variantIdentifiers';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';

function OrderShipmentForm(props) {
  const { maxQuantities } = props;
  const { quantities, trackingNumber, shippingMethod } = props.shipment;
  const fulfillmentLines = props.fulfillment.fulfillment_lines;
  const readonly = !!props.readonly;

  let initialCarrier = '';
  if (shippingMethod) {
    initialCarrier = Object.keys(ShippingMethodsByCarrier).find((key) =>
      Object.values(ShippingMethodsByCarrier[key]).includes(shippingMethod),
    );
  }
  const [carrier, setCarrier] = useState(initialCarrier);
  const carriersOptions = Object.values(Carriers).map((carrier) => ({
    label: CarriersDisplayName[carrier],
    value: carrier,
  }));
  carriersOptions.unshift({ label: 'Choose a Carrier', value: '' });

  const shippingMethodsOptions = carrier
    ? Object.keys(ShippingMethodsByCarrier[carrier]).map((shippingMethodName) => ({
        label: startCase(shippingMethodName),
        value: ShippingMethodsByCarrier[carrier][shippingMethodName],
      }))
    : [];
  shippingMethodsOptions.unshift({ label: 'Choose a Shipping Method', value: '' });

  function onChange(key, value) {
    props.onChange({
      [key]: value,
    });
  }

  function currentQuantityValue(fulfillmentLineId) {
    return quantities.hasOwnProperty(fulfillmentLineId)
      ? quantities[fulfillmentLineId]
      : maxQuantities[fulfillmentLineId];
  }

  function shippingMethodInput() {
    if (readonly) {
      if (!shippingMethod) {
        return null;
      }

      const carrier = findCarrierByShippingMethod(shippingMethod);
      const currentShippingMethods = ShippingMethodsByCarrier[carrier];
      const shippingMethodKey = currentShippingMethods
        ? Object.keys(currentShippingMethods).find((key) => currentShippingMethods[key] === shippingMethod)
        : undefined;
      return (
        <label data-testid="shipping-method-readonly">
          <strong>Shipping Method:</strong> <span>{CarriersDisplayName[carrier]}</span>{' '}
          <span>{startCase(shippingMethodKey)}</span>
        </label>
      );
    } else {
      return (
        <>
          <label>
            <strong>Carrier:</strong>
            <Select
              options={carriersOptions}
              value={carrier}
              onChange={(value) => {
                setCarrier(value);
                onChange('shippingMethod', '');
              }}
              testId="carrier"
            />
          </label>
          <label>
            <strong>Shipping Method:</strong>
            <Select
              options={shippingMethodsOptions}
              value={shippingMethod || ''}
              onChange={(value) => onChange('shippingMethod', value)}
              disabled={readonly || !carrier}
              testId="shipping-method"
            />
          </label>
        </>
      );
    }
  }

  function trackingNumberInput() {
    if (readonly) {
      return <span data-testid="tracking-number-readonly">{stringOrNAFormatter(trackingNumber)}</span>;
    } else {
      return (
        <Input
          value={trackingNumber}
          onChange={(e) => onChange('trackingNumber', e.target.value)}
          disabled={readonly}
          testId="tracking-number"
        />
      );
    }
  }

  return (
    <div className={cn(styles.shipment, readonly ? styles.shipmentReadonly : '')}>
      <table className={styles.shipmentLines}>
        <thead>
          <tr>
            <th className={styles.productsCol}>Product</th>
            <th className={styles.identifierCol}>{VariantIdentifierDisplayName[VARIANT_IDENTIFIER_SKU]}</th>
            <th>Ordered</th>
            <th>{readonly ? 'Shipped' : 'Ship'}</th>
          </tr>
        </thead>
        <tbody>
          {fulfillmentLines.map((fulfillmentLine) => (
            <tr key={fulfillmentLine.id} data-testid={`fulfillment-line-${fulfillmentLine.id}`}>
              <td className={styles.productsCol}>{fulfillmentLine.orderLine.variant.title}</td>
              <td className={styles.identifierCol}>{fulfillmentLine.orderLine.variant[VARIANT_IDENTIFIER_SKU]}</td>
              <td data-testid={`fulfillment-line-quantity-${fulfillmentLine.id}`}>
                {fulfillmentLine.original_quantity || fulfillmentLine.quantity}
              </td>
              <td className={styles.quantityCol}>
                {readonly ? (
                  quantities[fulfillmentLine.id] || NO_VALUE_PLACEHOLDER
                ) : (
                  <InputNumber
                    min={0}
                    max={maxQuantities[fulfillmentLine.id]}
                    value={currentQuantityValue(fulfillmentLine.id)}
                    onChange={(number) => onChange('quantities', { [fulfillmentLine.id]: number })}
                    formatter={numberFormatter}
                    data-testid={`fulfillment-line-input-${fulfillmentLine.id}`}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.shipmentInfo}>
        <div className={styles.shippingMethod}>{shippingMethodInput()}</div>
        <div className={styles.trackingNumber}>
          <label>
            <strong>Tracking Number:</strong> {trackingNumberInput()}
          </label>
        </div>
      </div>
    </div>
  );
}

OrderShipmentForm.propTypes = {
  maxQuantities: PropTypes.object,
  fulfillment: PropTypes.shape({
    fulfillment_lines: PropTypes.array.isRequired,
  }).isRequired,
  shipment: PropTypes.shape({
    quantities: PropTypes.object.isRequired,
    trackingNumber: PropTypes.string,
    shippingMethod: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  readonly: PropTypes.bool,
};

export default OrderShipmentForm;
